import React, { useState, useMemo } from "react";
import { KeyRound, Copy, Check, Printer } from "lucide-react";
import { PrintLoginCardModal } from "./PrintLoginCardModal";

const inputCls =
  "w-full rounded-md border bg-[color:var(--surface-card)] px-3 py-2 text-base transition-colors focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25";

const TICKET_PRICE = 100;

const formatUSD = (n) => `$${Number(n || 0).toLocaleString()}`;

const ResetPasswordModal = ({ user, onSubmit, onClose }) => {
  const [password, setPassword] = useState("");
  const [shownTemp, setShownTemp] = useState(""); // populated after a successful "generate" path
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const generateRandom = () => {
    // Friendly readable 8-char temp: avoids 0/O/l/1 confusion.
    const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let out = "";
    for (let i = 0; i < 8; i++) out += chars[Math.floor(Math.random() * chars.length)];
    setPassword(out);
    setErr("");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    setBusy(true);
    const res = await onSubmit(password);
    setBusy(false);
    if (!res.ok) { setErr(res.error || "Could not reset."); return; }
    // Custom password path → confirm and close. We don't show it back (admin typed it).
    onClose(res);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shownTemp || password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* ignored */ }
  };

  return (
    <div
      data-testid="reset-pw-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(11,18,20,0.55)" }}
      onClick={onClose}
    >
      <form onSubmit={submit} className="bg-[color:var(--surface-card)] rounded-2xl shadow-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold text-[color:var(--ink-primary)]">Reset password for {user.name}</h3>
        <p className="mt-1 text-sm text-[color:var(--ink-tertiary)]">
          Enter a new password (at least 6 characters) — or use <button
            type="button"
            data-testid="reset-pw-generate"
            onClick={generateRandom}
            className="text-[color:var(--brand-primary)] font-semibold hover:underline"
          >Generate random</button>. Share it securely with the user.
        </p>

        <label className="block mt-4 text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-tertiary)] mb-1.5">New password</label>
        <input
          data-testid="reset-pw-input"
          type="text"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 6 characters"
          className="w-full rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-card)] px-3 py-2 text-base font-mono focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
        />

        {password && (
          <button
            type="button"
            data-testid="reset-pw-copy"
            onClick={copy}
            className="mt-2 px-3 py-1.5 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-card)] text-xs font-semibold text-[color:var(--ink-secondary)] hover:bg-[color:var(--surface-subtle)] inline-flex items-center gap-1.5"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy to clipboard"}
          </button>
        )}

        {err && <p className="text-sm font-medium text-rose-700 mt-3">{err}</p>}

        <div className="flex gap-2 mt-4">
          <button
            type="submit"
            data-testid="reset-pw-submit"
            disabled={busy || password.length < 6}
            className="flex-1 px-4 py-2 rounded-lg bg-[color:var(--brand-primary)] text-white text-sm font-semibold hover:bg-[color:var(--brand-primary-hover)] disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save new password"}
          </button>
          <button
            type="button"
            data-testid="reset-pw-cancel"
            onClick={() => onClose(null)}
            disabled={busy}
            className="px-4 py-2 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-card)] text-[color:var(--ink-secondary)] text-sm font-semibold hover:bg-[color:var(--surface-subtle)] disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

const EditUserRow = ({ user, onSave, onCancel, showToast }) => {
  const [name, setName] = useState(user.name || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [role, setRole] = useState(user.role || "seller");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const patch = {};
    if (name.trim() !== (user.name || "")) patch.name = name.trim();
    if (phone.trim() !== (user.phone || "")) patch.phone = phone.trim();
    if (role !== user.role) patch.role = role;
    if (password.trim().length > 0) {
      if (password.trim().length < 6) {
        setError("Password must be at least 6 characters");
        return;
      }
      patch.password = password.trim();
    }
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    setSaving(true);
    const res = await onSave(user.id, patch);
    setSaving(false);
    if (!res.ok) {
      setError(res.error || "Could not update.");
      return;
    }
    showToast(`${name.trim() || user.name} updated`);
    onCancel();
  };

  return (
    <form onSubmit={submit} className="py-4 space-y-3" data-testid={`edit-user-form-${user.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium mb-1 block text-[color:var(--ink-tertiary)]">Full name</label>
          <input data-testid={`edit-name-${user.id}`} value={name} onChange={(e) => setName(e.target.value)} className={inputCls} style={{ borderColor: "var(--border-default)" }} />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block text-[color:var(--ink-tertiary)]">Contact phone</label>
          <input data-testid={`edit-phone-${user.id}`} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" className={inputCls} style={{ borderColor: "var(--border-default)" }} />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block text-[color:var(--ink-tertiary)]">Role</label>
          <select data-testid={`edit-role-${user.id}`} value={role} onChange={(e) => setRole(e.target.value)} className={inputCls} style={{ borderColor: "var(--border-default)" }}>
            <option value="seller">Seller</option>
            <option value="viewer">Viewer</option>
            <option value="checkin_volunteer">Check-in Volunteer</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block text-[color:var(--ink-tertiary)]">New password (optional)</label>
          <input data-testid={`edit-password-${user.id}`} type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave blank to keep current" className={inputCls} style={{ borderColor: "var(--border-default)" }} />
        </div>
      </div>
      {error && <p className="text-sm font-medium text-rose-700">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={saving} data-testid={`save-user-${user.id}`} className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90 disabled:opacity-50" style={{ background: "var(--semantic-success)" }}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={onCancel} data-testid={`cancel-edit-${user.id}`} className="px-4 py-2 text-sm font-medium rounded-md border bg-[color:var(--surface-card)] transition-colors" style={{ borderColor: "var(--border-default)", color: "var(--ink-primary)" }}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export const SellersTab = ({ users, tickets, currentUser, onAdd, onRemove, onUpdate, onResetPassword, showToast }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("seller");
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [resetTarget, setResetTarget] = useState(null); // { user } — open modal for this user
  const [printTarget, setPrintTarget] = useState(null); // { user, password? } — open print card modal
  const isAdmin = currentUser?.role === "admin";

  // Per-seller settlement counts derived from CURRENT ticket state.
  // collected = funds_received count × $100, pending = sold count × $100
  const settlement = useMemo(() => {
    const acc = {}; // { sellerName: { collected: n, pending: n } }
    if (!tickets) return acc;
    for (let i = 1; i <= 400; i++) {
      const t = tickets[i];
      if (!t) continue;
      const nm = t.assigned_seller_name;
      if (!nm) continue;
      if (t.status === "funds_received") {
        acc[nm] = acc[nm] || { collected: 0, pending: 0 };
        acc[nm].collected += 1;
      } else if (t.status === "sold") {
        acc[nm] = acc[nm] || { collected: 0, pending: 0 };
        acc[nm].pending += 1;
      }
    }
    return acc;
  }, [tickets]);

  const openReset = (u) => setResetTarget({ user: u });

  const submitReset = async (password) => {
    const res = await onResetPassword(resetTarget.user.id, password);
    // Stash the password we know for the follow-up print-card offer.
    if (res.ok) res._known_password = password;
    return res;
  };

  const closeReset = (result) => {
    const target = resetTarget;
    setResetTarget(null);
    if (result?.ok) {
      showToast(`Password updated for ${target.user.name}`);
      // Offer to print the login card with the fresh password preloaded.
      if (result._known_password) {
        setPrintTarget({ user: target.user, password: result._known_password });
      }
    }
  };

  const add = async (e) => {
    e.preventDefault();
    setError("");
    const res = await onAdd({ email, password, name, phone, role });
    if (!res.ok) { setError(res.error || "Could not add."); return; }
    showToast(`${res.seller.name} added as ${res.seller.role}`);
    // Offer to print the login card immediately — password is still in state here.
    setPrintTarget({ user: { ...res.seller, name, email }, password });
    setEmail(""); setName(""); setPhone(""); setPassword(""); setRole("seller");
  };

  const sellers = users;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {isAdmin && (
          <form onSubmit={add} className="bg-[color:var(--surface-card)] border rounded-lg p-5" style={{ borderColor: "var(--border-default)" }}>
            <h2 className="text-base font-semibold mb-4" style={{ color: "var(--ink-primary)" }}>Add a user account</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block" style={{ color: "var(--ink-primary)" }}>Full name</label>
                <input data-testid="seller-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls} style={{ borderColor: "var(--border-default)" }} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block" style={{ color: "var(--ink-primary)" }}>Email</label>
                <input data-testid="seller-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" className={inputCls} style={{ borderColor: "var(--border-default)" }} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block" style={{ color: "var(--ink-primary)" }}>Contact phone (optional)</label>
                <input data-testid="seller-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" className={inputCls} style={{ borderColor: "var(--border-default)" }} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block" style={{ color: "var(--ink-primary)" }}>Role</label>
                <select data-testid="seller-role-input" value={role} onChange={(e) => setRole(e.target.value)} className={inputCls} style={{ borderColor: "var(--border-default)" }}>
                  <option value="seller">Seller — sells assigned tickets only</option>
                  <option value="viewer">Viewer — read-only access to everything</option>
                  <option value="checkin_volunteer">Check-in Volunteer — drawing-night check-in only</option>
                  <option value="admin">Admin — full control</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block" style={{ color: "var(--ink-primary)" }}>Initial password</label>
                <input data-testid="seller-password-input" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className={inputCls} style={{ borderColor: "var(--border-default)" }} />
                <p className="text-xs mt-1.5" style={{ color: "var(--ink-tertiary)" }}>Share this securely; the user can change it after first login.</p>
              </div>
              {error && <p className="text-sm font-medium text-rose-700">{error}</p>}
              <button data-testid="add-seller-btn" type="submit" className="px-5 py-2.5 text-sm font-medium text-white rounded-md transition-colors hover:opacity-90" style={{ background: "var(--semantic-success)" }}>Add user</button>
            </div>
          </form>
        )}

        <div className={`bg-[color:var(--surface-card)] border rounded-lg p-5 ${!isAdmin ? "lg:col-span-2" : ""}`} style={{ borderColor: "var(--border-default)" }}>
          <h2 className="text-base font-semibold mb-4" style={{ color: "var(--ink-primary)" }}>Active users</h2>
          {sellers.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--ink-tertiary)" }}>No users yet.</p>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--border-default)" }}>
              {sellers.map((s) => {
                const isEditing = isAdmin && editingId === s.id;
                const st = settlement[s.name];
                return (
                  <li key={s.id} className="py-3" data-testid={`user-row-${s.id}`}>
                    {!isEditing ? (
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="font-semibold" style={{ color: "var(--ink-primary)" }}>{s.name}</p>
                          {s.email && <p className="text-sm text-[color:var(--ink-tertiary)]">{s.email}</p>}
                          {s.phone && <p className="text-sm text-[color:var(--ink-tertiary)]">{s.phone}</p>}
                          {s.role === "seller" && st && (
                            <div className="mt-2 flex items-center gap-3 text-xs" data-testid={`settlement-${s.id}`}>
                              <span className="inline-flex items-center gap-1.5">
                                <span className="inline-block w-2 h-2 rounded-full bg-[color:var(--semantic-danger)]" aria-hidden />
                                <span className="font-semibold text-[color:var(--ink-primary)]">{formatUSD(st.collected * TICKET_PRICE)}</span>
                                <span className="text-[color:var(--ink-tertiary)]">collected ({st.collected})</span>
                              </span>
                              <span className="inline-flex items-center gap-1.5">
                                <span className="inline-block w-2 h-2 rounded-full bg-[color:var(--status-sold-bg)]" aria-hidden />
                                <span className="font-semibold text-[color:var(--ink-primary)]">{formatUSD(st.pending * TICKET_PRICE)}</span>
                                <span className="text-[color:var(--ink-tertiary)]">pending ({st.pending})</span>
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          <span className="text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: s.role === "admin" ? "var(--brand-primary)" : s.role === "viewer" ? "var(--brand-primary)" : s.role === "checkin_volunteer" ? "var(--brand-accent)" : "var(--semantic-success)", color: "var(--ink-on-brand)" }}>{s.role === "checkin_volunteer" ? "check-in" : s.role}</span>
                          {isAdmin && (
                            <button data-testid={`edit-user-${s.id}`} onClick={() => setEditingId(s.id)} className="px-3 py-1.5 text-xs font-semibold rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-card)] text-[color:var(--ink-secondary)] transition-colors hover:border-[color:var(--brand-primary)] hover:text-[color:var(--brand-primary)]">
                              Edit
                            </button>
                          )}
                          {isAdmin && s.id !== currentUser.id && (
                            <button
                              data-testid={`reset-pw-${s.id}`}
                              onClick={() => openReset(s)}
                              className="px-3 py-1.5 text-xs font-semibold rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-card)] text-[color:var(--ink-secondary)] transition-colors hover:border-amber-500 hover:text-amber-700 inline-flex items-center gap-1"
                            >
                              <KeyRound className="h-3 w-3" />
                              Reset password
                            </button>
                          )}
                          {isAdmin && s.id !== currentUser.id && (
                            <button
                              data-testid={`print-card-${s.id}`}
                              onClick={() => setPrintTarget({ user: s })}
                              className="px-3 py-1.5 text-xs font-semibold rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-card)] text-[color:var(--ink-secondary)] transition-colors hover:border-[color:var(--brand-primary)] hover:text-[color:var(--brand-primary)] inline-flex items-center gap-1"
                              title="Print a one-page login card for this user"
                            >
                              <Printer className="h-3 w-3" />
                              Print card
                            </button>
                          )}
                          {isAdmin && s.id !== currentUser.id && (
                            <button data-testid={`remove-seller-${s.id}`} onClick={() => onRemove(s.id)} className="px-3 py-1.5 text-xs font-semibold rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-card)] text-[color:var(--ink-secondary)] transition-colors hover:border-rose-500 hover:text-rose-700">
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <EditUserRow
                        user={s}
                        onSave={onUpdate}
                        onCancel={() => setEditingId(null)}
                        showToast={showToast}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {resetTarget && (
        <ResetPasswordModal
          user={resetTarget.user}
          onSubmit={submitReset}
          onClose={closeReset}
        />
      )}

      {printTarget && (
        <PrintLoginCardModal
          user={printTarget.user}
          initialPassword={printTarget.password || ""}
          onClose={() => setPrintTarget(null)}
        />
      )}
    </section>
  );
};
